import { Trophy, User } from 'lucide-react'
import { NavLink, Outlet, useLocation } from 'react-router'
import { primeAudio } from '../lib/feedback'
import { hasPhoneNav } from '../lib/games'
import { ArenaNotices } from './arena/ArenaNotices'
import { LevelPill } from './LevelPill'
import { OnlinePill } from './OnlinePill'
import { SoundToggle } from './SoundToggle'
import { UpdateBanner } from './UpdateBanner'

function navClass({ isActive }: { isActive: boolean }, slim: boolean) {
  return [
    // `hidden` and `inline-flex` both set display, so the slim variant swaps rather than stacks them.
    slim ? 'hidden lg:inline-flex' : 'inline-flex',
    'items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
    isActive
      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
      : 'text-slate-600 hover:bg-slate-200/70 dark:text-slate-300 dark:hover:bg-slate-800',
  ].join(' ')
}

export function Layout() {
  // Pages with their own phone bottom bar keep only logo + sound in the header.
  const slim = hasPhoneNav(useLocation().pathname)
  const link = (state: { isActive: boolean }) => navClass(state, slim)
  return (
    <div className="flex min-h-dvh flex-col" onPointerDownCapture={primeAudio}>
      <UpdateBanner />
      <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-slate-50/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-2.5">
          <NavLink to="/" className="flex items-center gap-2 font-bold tracking-tight">
            <Logo />
            <span className="hidden sm:inline">XOX Arena</span>
          </NavLink>
          <nav className="flex items-center gap-1" aria-label="Main">
            <NavLink to="/" end className={link}>
              Play
            </NavLink>
            <NavLink to="/leaderboard" className={link}>
              <Trophy className="size-4" aria-hidden />
              <span className="hidden sm:inline">Leaderboard</span>
            </NavLink>
            <NavLink to="/me" className={link}>
              <User className="size-4" aria-hidden />
              <span className="hidden sm:inline">Profile</span>
            </NavLink>
            <OnlinePill />
            <LevelPill />
            <SoundToggle />
          </nav>
        </div>
        <ArenaNotices />
      </header>
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col px-4 pb-6 pt-4">
        <Outlet />
      </main>
    </div>
  )
}

function Logo() {
  return (
    <svg viewBox="0 0 64 64" className="size-7" aria-hidden>
      <rect width="64" height="64" rx="14" className="fill-slate-900 dark:fill-slate-100" />
      <path
        d="M16 16 L30 30 M30 16 L16 30"
        className="stroke-x"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle cx="43" cy="43" r="8" fill="none" className="stroke-o" strokeWidth="6" />
      <path
        d="M37 16 L48 27 M48 16 L37 27"
        className="stroke-x"
        strokeWidth="6"
        strokeLinecap="round"
        opacity="0.4"
      />
    </svg>
  )
}
